import { APIRoutes } from "@/api";
import { Button, ButtonProps } from "@/components/ui/button";
import { useToggle } from "@/hooks/use-toggle";
import { formatJsError } from "@/lib/format-js-error";
import { cn } from "@/lib/utils";
import { PortfolioSource } from "@/services/portfolios/schema";
import { useJobsStore } from "@/stores/jobs-store";
import { formatElapsedTime } from "@/utils/format-date";
import { PortfolioSourceType } from "@prisma/client";
import { RefreshCwIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";
import { toast } from "sonner";

interface PortfolioSyncButtonProps {
  readonly portfolioName: string;
  readonly portfolioId: string;
  readonly sources: PortfolioSource[];
  readonly size?: ButtonProps["size"];
  readonly variant?: ButtonProps["variant"];
  readonly remainingSyncCount: number;
}

export function PortfolioSyncButton(props: PortfolioSyncButtonProps) {
  const t = useTranslations("Portfolio.nav");
  const loadingToggle = useToggle();
  const jobsStore = useJobsStore();
  const [jobId, setJobId] = React.useState<string | null>(null);

  const isWalletSource = props.sources.some(
    (source) => source.type === PortfolioSourceType.CRYPTO_WALLET,
  );
  const isExchangeSource = props.sources.some(
    (source) => source.type === PortfolioSourceType.EXCHANGE_API,
  );
  const syncEndpoint = isWalletSource
    ? APIRoutes.JOB_WALLET_SYNC
    : isExchangeSource
      ? APIRoutes.JOB_EXCHANGE_SYNC
      : null;

  const isSyncAllowed = props.remainingSyncCount > 0;

  // const reloadPage = async () => {
  //   await revalidateUserPortfolioPaths({
  //     path: Routes.PORTFOLIO(props.portfolioId),
  //     redirect: true,
  //   });
  //   modalToggle.setOff();
  // };

  const syncPortfolio = async () => {
    if (!isSyncAllowed) {
      const midnight = new Date();
      midnight.setHours(23, 59, 59, 999);
      const time = formatElapsedTime(midnight.getTime() - Date.now());
      toast.error(t("sync.error.limit", { time }), {
        description: `(${new Date(midnight.getTime()).toLocaleString()})`,
      });
      return;
    }

    loadingToggle.setOn();

    if (!syncEndpoint) {
      toast.error(t("sync.error.sourceType"));
      return;
    }
    try {
      const response = await fetch(syncEndpoint, {
        method: "POST",
        body: JSON.stringify({
          portfolioId: props.portfolioId,
        }),
      });

      const data = (await response.json()) as { jobId: string };
      if (!data) {
        toast.error(`An error occurred: ${response.statusText}`);
        return;
      }

      if (data.jobId) {
        const jobId = String(data.jobId);
        const sourceType = isWalletSource ? "wallet" : "exchange";
        useJobsStore.getState().addJob(jobId, {
          name: `${props.portfolioName} sync`,
          status: "queued",
          componentId: `portfolio-sync-${sourceType}`,
        });
        setJobId(jobId);
      }
    } catch (error) {
      const msg = formatJsError(error);
      toast.error(msg);
      loadingToggle.setOff();
    }
  };

  React.useEffect(
    function trackLoadingStateForJobStatus() {
      if (!jobId) {
        return;
      }

      const jobsMeta = jobsStore.jobs.get(jobId);
      if (jobsMeta) {
        loadingToggle.setOn();
        return;
      } else {
        loadingToggle.setOff();
      }
    },
    [jobId, jobsStore.jobs, loadingToggle],
  );

  if (!isWalletSource && !isExchangeSource) {
    return null;
  }

  return (
    <Button
      type="button"
      size={props.size ?? "sm"}
      className="mr-1 gap-2"
      variant={props.variant ?? "outline"}
      onClick={syncPortfolio}
      disabled={loadingToggle.value}
      aria-disabled={loadingToggle.value}
    >
      <RefreshCwIcon
        className={cn(
          "size-4",
          loadingToggle.value ? "animate-spin [animation-duration:2s]" : "",
        )}
      />
      {t("sync.label")}
    </Button>
  );
}
